import type Phaser from 'phaser';

/**
 * Status pill for spectator mode. Sits just under the bankroll bezel at
 * the top of the table, mirrors the dark-felt + gold-stripe styling of
 * BackToFloorPill so it visually belongs to the same control language.
 *
 * Two modes:
 *   - 'live':   "● LIVE · WATCHING" — producer is actively publishing.
 *   - 'replay': "REPLAY · CRAPS"   — producer has ended the session.
 */
export interface SpectatorPillOptions {
  scene: Phaser.Scene;
  centerX: number;
  topY: number;
  /** Scale multiplier for px sizes (default 1). */
  scale?: number;
  initialMode: 'live' | 'replay';
}

const GOLD = '#d4af37';
const DARK = 0x06100c;

export class SpectatorPill {
  private scene: Phaser.Scene;
  private bg: Phaser.GameObjects.Graphics;
  private label: Phaser.GameObjects.Text;
  private centerX: number;
  private topY: number;
  private padX: number;
  private padY: number;
  private stripeW: number;
  private fontSize: number;

  constructor(opts: SpectatorPillOptions) {
    const { scene, centerX, topY, initialMode } = opts;
    const sm = opts.scale ?? 1;
    this.scene = scene;
    this.centerX = centerX;
    this.topY = topY;
    this.padX = 10 * sm;
    this.padY = 5 * sm;
    this.stripeW = 2 * sm;
    this.fontSize = Math.round(11 * sm);

    this.bg = scene.add.graphics();
    this.label = scene.add
      .text(0, 0, '', {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${this.fontSize}px`,
        color: GOLD,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0);

    this.setMode(initialMode);
  }

  setMode(mode: 'live' | 'replay'): void {
    const text = mode === 'live' ? '● LIVE · WATCHING' : 'REPLAY · CRAPS';
    this.label.setText(text);
    const w = this.label.width + this.padX * 2;
    const h = this.label.height + this.padY * 2;
    const x = Math.round(this.centerX - w / 2);
    const y = Math.round(this.topY);
    this.label.setPosition(x + this.padX, y + this.padY);
    this.bg.clear();
    this.bg.fillStyle(DARK, 0.7);
    this.bg.fillRect(x, y, w, h);
    // Gold accent stripe on the left edge — mirrors BackToFloorPill so the
    // pill reads as part of the same gold-on-dark control family.
    this.bg.fillStyle(0xd4af37, 1);
    this.bg.fillRect(x, y, this.stripeW, h);
    // Set explicit depth so the pill sits above the bezel below it.
    this.bg.setDepth(50);
    this.label.setDepth(51);
    void this.scene;
  }

  destroy(): void {
    this.bg.destroy();
    this.label.destroy();
  }
}
